import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { WhatsappLogo, Envelope, Phone, ArrowLeft, ChatCircle, ShoppingCart, CurrencyInr, Bell, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import AttachmentsPanel from "@/components/AttachmentsPanel";
import { usePermissions } from "@/lib/permissions";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";

const iconFor = (t) => ({
  note: ChatCircle, call: Phone, whatsapp: WhatsappLogo, email: Envelope,
  order: ShoppingCart, payment: CurrencyInr, followup: Bell, stage_change: ArrowLeft
}[t] || ChatCircle);

export default function CustomerDetail() {
  const { id } = useParams();
  const { canEdit, can } = usePermissions();
  const [customer, setCustomer] = useState(null);
  const [events, setEvents] = useState([]);
  const [note, setNote] = useState("");
  const [waOpen, setWaOpen] = useState(false);
  const [waMsg, setWaMsg] = useState("");
  const [waSending, setWaSending] = useState(false);

  const load = async () => {
    const [c, t] = await Promise.all([api.get(`/customers/${id}`), api.get(`/timeline/${id}`)]);
    setCustomer(c.data); setEvents(t.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const addNote = async () => {
    if (!note.trim()) return;
    await api.post("/timeline", { customer_id: id, type: "note", title: "Note added", description: note });
    setNote(""); toast.success("Note added"); load();
  };

  if (!customer) return <div className="p-8 text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="customer-detail-page">
      <Link to="/customers" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft size={16} /> Back to customers
      </Link>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-6 lg:col-span-1">
          <div className="w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-heading text-xl font-bold">
            {customer.customer_name?.[0]?.toUpperCase()}
          </div>
          <h2 className="font-heading font-bold text-2xl mt-4 leading-tight">{customer.customer_name}</h2>
          <p className="text-sm text-muted-foreground">{customer.company_name}</p>
          <div className="space-y-3 mt-6 text-sm">
            <div><div className="overline text-muted-foreground mb-1">Mobile</div><div>{customer.mobile}</div></div>
            <div><div className="overline text-muted-foreground mb-1">Email</div><div>{customer.email || "—"}</div></div>
            <div><div className="overline text-muted-foreground mb-1">GST</div><div className="font-mono text-xs">{customer.gst || "—"}</div></div>
            <div><div className="overline text-muted-foreground mb-1">City</div><div>{customer.city}, {customer.state}</div></div>
            <div><div className="overline text-muted-foreground mb-1">Category</div><div>{customer.category}</div></div>
            <div><div className="overline text-muted-foreground mb-1">Source</div><div>{customer.source}</div></div>
          </div>
          <div className="flex gap-2 mt-6 flex-wrap">
            {customer.whatsapp && <a href={`https://wa.me/${customer.whatsapp}`} target="_blank" rel="noreferrer"><Button variant="outline" size="sm"><WhatsappLogo size={14} className="mr-1" />WhatsApp</Button></a>}
            {customer.email && <a href={`mailto:${customer.email}`}><Button variant="outline" size="sm"><Envelope size={14} className="mr-1" />Email</Button></a>}
            <a href={`tel:${customer.mobile}`}><Button variant="outline" size="sm"><Phone size={14} className="mr-1" />Call</Button></a>
            {can("whatsapp") && (customer.whatsapp || customer.mobile) && (
              <Dialog open={waOpen} onOpenChange={setWaOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="rounded-full" data-testid="send-wa-open">
                    <PaperPlaneTilt size={14} className="mr-1" />Send via CRM
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader><DialogTitle>Send WhatsApp via Business API</DialogTitle></DialogHeader>
                  <div className="space-y-3 py-2">
                    <div className="text-xs text-muted-foreground">To: <span className="font-mono">{customer.whatsapp || customer.mobile}</span></div>
                    <Textarea rows={5} value={waMsg} onChange={(e) => setWaMsg(e.target.value)}
                      placeholder="Hi Rahul, thanks for your interest…" data-testid="send-wa-body" />
                    <p className="text-[11px] text-muted-foreground">Uses the WhatsApp Business config saved in Integrations. Outside the 24-hour service window use a pre-approved template.</p>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setWaOpen(false)}>Cancel</Button>
                    <Button disabled={waSending || !waMsg.trim()} data-testid="send-wa-submit"
                      onClick={async () => {
                        setWaSending(true);
                        try {
                          await api.post("/integrations/whatsapp/send-text", {
                            to: customer.whatsapp || customer.mobile,
                            body: waMsg,
                            customer_id: customer.id,
                          });
                          toast.success("WhatsApp sent");
                          await api.post("/timeline", { customer_id: customer.id, type: "whatsapp",
                            title: "WhatsApp sent via CRM", description: waMsg.slice(0, 200) }).catch(() => {});
                          setWaOpen(false); setWaMsg(""); load();
                        } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
                        finally { setWaSending(false); }
                      }}>{waSending ? "Sending…" : "Send message"}</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
          <div className="mt-6 pt-6 border-t border-border/40">
            <div className="overline text-muted-foreground mb-3">Documents & Files</div>
            <AttachmentsPanel entityType="customer" entityId={customer.id} dense />
          </div>
        </Card>

        <Card className="p-6 lg:col-span-2">
          <div className="overline text-muted-foreground mb-1">Activity</div>
          <h3 className="font-heading font-bold text-xl mb-4">Timeline</h3>

          {canEdit("customers.edit", customer) && (
            <div className="flex gap-2 mb-6">
              <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note about this customer…"
                className="flex-1" data-testid="note-input" />
              <Button onClick={addNote} data-testid="add-note-button" className="rounded-full self-end">Add note</Button>
            </div>
          )}

          <div className="space-y-4">
            {events.map((ev) => {
              const Ic = iconFor(ev.type);
              return (
                <div key={ev.id} className="flex gap-3 pb-4 border-b border-border/40 last:border-0">
                  <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center shrink-0">
                    <Ic size={14} weight="duotone" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-medium text-sm">{ev.title}</div>
                      <div className="text-xs text-muted-foreground">{new Date(ev.created_at).toLocaleString("en-IN")}</div>
                    </div>
                    {ev.description && <div className="text-sm text-muted-foreground mt-1">{ev.description}</div>}
                  </div>
                </div>
              );
            })}
            {!events.length && <div className="text-center text-muted-foreground py-8">No activity yet — add a note above.</div>}
          </div>
        </Card>
      </div>
    </div>
  );
}
